/* ============================================================
   BloomUp — cart drawer pay button controller

   Sole authority on which pay button is shown:

     Cart empty             → no button at all
     Country = India        → GoKwik button (always, whatever the visitor's IP)
     Any other country      → native Shopify "Pay Now"
     India but GoKwik markup missing → native "Pay Now", so the customer is
                                       never left without a way to pay

   <html> carries .bu-market-intl for non-India markets. Liquid sets it at
   render time so CSS can hide the GoKwik button from the first paint (no
   flicker on re-render), and this script keeps it in sync afterwards, since
   country switching happens without a page reload.

   SWITCHING BACK TO INDIA (fix, 12 Sep 2026)
   ------------------------------------------
   GoKwik's SDK only initialises for the India market. If the page first
   rendered in another country, .gokwik-checkout is hidden from first paint
   and GoKwik never builds its button. Switching country afterwards does NOT
   reload the page — bloomup-cart.js posts to /localization and then calls
   partialRerender, which only swaps three containers. So data-bu-country
   becomes IN and the html class is corrected, but the GoKwik button still
   does not exist, and the customer is left on the native fallback (or, if the
   fallback host has been moved into the Order Summary sheet, with no visible
   button at all).

   Nothing in the page can initialise GoKwik after the fact, so the only
   reliable repair is a one-time reload on that specific transition:
   switched TO India, and after a grace period the GoKwik button is still
   absent. Guarded by a sessionStorage flag so it can never loop, and only
   armed by an actual country-select change — never on a normal India page
   load where GoKwik is merely slow to appear.
   ============================================================ */
(function () {
  "use strict";

  var LABEL = "Pay Now";
  var RELOAD_KEY = "bu-gk-reloaded";
  var GRACE_MS = 2500;
  var switchedToIndiaAt = 0;

  function drawerEl() { return document.querySelector("#CartDrawer"); }

  function drawerOpen() {
    var d = drawerEl();
    if (!d) return false;
    if (d.hasAttribute("open") || d.open) return true;
    if (d.classList.contains("is--opened")) return true;
    var r = d.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  /* is the cart actually empty? */
  function cartEmpty() {
    var d = drawerEl();
    if (!d) return true;
    if (d.querySelector(".hdt-mini-cart__item")) return false;
    /* the theme renders a dedicated empty state */
    if (d.querySelector(".hdt-mini-cart__emty, .hdt-mini-cart__empty")) return true;
    var host = document.querySelector("hdt-cart-drawer");
    if (host) {
      var count = parseInt(host.getAttribute("data-count"), 10);
      if (!isNaN(count)) return count === 0;
    }
    return true;
  }

  function country() {
    var d = drawerEl();
    if (!d) return null;
    var root = d.querySelector("[data-bu-cart-root]");
    if (root) return root.getAttribute("data-bu-country");
    var sel = d.querySelector(".bu-country-select");
    return sel ? sel.value : null;
  }

  function buttonHost() {
    var d = drawerEl();
    if (!d) return null;
    var wrap = d.querySelector(".hdt-mini-cart__view-checkout");
    if (wrap) return wrap;
    var bottom =
      d.querySelector(".hdt-mini-cart__bottom-wrap") ||
      d.querySelector(".hdt-mini-cart__bottom");
    if (!bottom) return null;
    wrap = document.createElement("div");
    wrap.className = "hdt-mini-cart__view-checkout bu-made-host";
    bottom.appendChild(wrap);
    return wrap;
  }

  function nativeButton(wrap) {
    var d = drawerEl();
    var btn = d.querySelector(".bu-native-checkout");
    if (!btn) {
      btn = document.createElement("a");
      btn.className = "bu-native-checkout";
      btn.setAttribute("href", "/checkout");
      btn.textContent = LABEL;
      wrap.appendChild(btn);
    } else if (!btn.parentNode) {
      wrap.appendChild(btn);
    }
    if (btn.textContent.trim() !== LABEL) btn.textContent = LABEL;
    return btn;
  }

  function show(el) {
    if (!el) return;
    el.style.setProperty("display", "flex", "important");
    el.style.setProperty("visibility", "visible", "important");
    el.style.setProperty("opacity", "1", "important");
  }
  function hide(el) {
    if (!el) return;
    el.style.setProperty("display", "none", "important");
  }

  /* one-time reload after switching to India with no GoKwik button */
  function maybeReloadForGokwik() {
    if (!switchedToIndiaAt) return;
    if (Date.now() - switchedToIndiaAt < GRACE_MS) return;
    try {
      if (window.sessionStorage.getItem(RELOAD_KEY) === "1") return;
      window.sessionStorage.setItem(RELOAD_KEY, "1");
    } catch (e) {
      /* storage blocked — do not reload rather than risk a loop */
      switchedToIndiaAt = 0;
      return;
    }
    switchedToIndiaAt = 0;
    window.location.reload();
  }

  function apply() {
    var d = drawerEl();
    if (!d) return;

    var gkWrap = d.querySelector(".gokwik-checkout");
    var existingNative = d.querySelector(".bu-native-checkout");

    /* nothing in the cart — show no pay button at all */
    if (cartEmpty()) {
      hide(gkWrap);
      hide(existingNative);
      return;
    }

    var wrap = buttonHost();
    if (!wrap) return;

    var c = country();
    var isIN = c === "IN";

    /* keep the render-time class honest after a no-reload country switch */
    if (c) {
      document.documentElement.classList.toggle("bu-market-intl", !isIN);
    }

    var gkBtn = d.querySelector(".gokwik-checkout button");

    if (isIN && gkBtn) {
      switchedToIndiaAt = 0;           /* GoKwik is present — nothing to repair */
      if (gkWrap) {
        gkWrap.style.removeProperty("display");
        gkWrap.style.removeProperty("visibility");
      }
      gkBtn.style.removeProperty("display");
      if (!gkBtn.classList.contains("gkactive2")) gkBtn.classList.add("gkactive2");
      if (gkBtn.disabled) gkBtn.disabled = false;
      gkBtn.classList.remove("disabled");
      hide(existingNative);
      return;
    }

    /* India but GoKwik never initialised — show the fallback now so the
       customer is never stranded, then reload once to get GoKwik back */
    hide(gkWrap);
    show(nativeButton(wrap));
    if (isIN) maybeReloadForGokwik();
  }

  function tick() {
    if (drawerOpen()) apply();
  }

  function init() {
    tick();
    setInterval(tick, 1500);

    var host = document.querySelector("hdt-cart-drawer");
    if (host && window.MutationObserver) {
      /* synchronous, so the button is rebuilt in the same frame the footer
         is replaced — a re-render cannot leave a visible gap */
      new MutationObserver(function () { tick(); }).observe(host, {
        childList: true,
        subtree: true
      });
    }
    var d = drawerEl();
    if (d && window.MutationObserver) {
      new MutationObserver(tick).observe(d, { attributes: true, attributeFilter: ["open", "class"] });
    }
    window.addEventListener("gokwikLoaded", function () { setTimeout(tick, 100); });
    document.addEventListener("change", function (e) {
      if (e.target && e.target.classList && e.target.classList.contains("bu-country-select")) {
        /* arm the repair only when switching INTO India */
        if (e.target.value === "IN") switchedToIndiaAt = Date.now();
        [300, 900, 1800, 3000].forEach(function (ms) { setTimeout(tick, ms); });
      }
    }, true);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();